import { useEffect, useMemo, useState } from "react";
import { useDynamicRateContext } from "../../../context";
import { isStringEmpty } from "../../../utils/handler_validations";
import styles from "./styles";
import RatePreviewChart from "../../common/RatePreviewChart";
import {
  ColorPicker,
  GeofenceMap,
  InputSearch,
} from "@wallavi/very-geofencing-library";
import { VeryBtnFill } from "../../common/v2/VeryBtnFill";
import { classVeryBtnFill } from "../../common/v2/VeryBtnFill/classVeryBtnFill";
import { classVeryIconGoogle } from "../../common/v2/VeryIconGoogle/classVeryIconGoogle";
import { VeryIconGoogle } from "../../common/v2/VeryIconGoogle";
import { VerySwitch } from "../../common/v2/VerySwitch";
import CustomSelect from "../../common/custom-select";
import { VeryInput } from "../../common/v2/VeryInput";
import { classVeryInput } from "../../common/v2/VeryInput/classVeryInput";
import {
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";

const propsTemplate = () => ({
  isLoaded: false,
});

const computeCentroid = (polygons) => {
  if (!Array.isArray(polygons) || polygons.length < 3) return null;
  const lat = polygons.reduce((s, p) => s + p.lat, 0) / polygons.length;
  const lng = polygons.reduce((s, p) => s + p.lng, 0) / polygons.length;
  return { lat, lng };
};

const ContainerRightRate = (props = propsTemplate()) => {
  const rates = useDynamicRateContext();
  const [confirmDynamicOpen, setConfirmDynamicOpen] = useState(false);

  const mapCenter = useMemo(() => {
    const centroid = computeCentroid(rates.data.polygons);
    return centroid ?? rates.data.ubicationCoordinates;
  }, [rates.data.polygons, rates.data.ubicationCoordinates]);

  const splitHour = (time) => {
    if (!time) return { hour: "", minute: "", period: "AM" };
    const regex = /^(\d{1,2}):(\d{2})\s?(AM|PM)$/i;
    const match = time.match(regex);

    if (!match) return { hour: "", minute: "", period: "AM" };

    return {
      hour: match[1],
      minute: match[2],
      period: match[3].toUpperCase(),
    };
  };
  const [startHourObj, setStartHourObj] = useState(
    () =>
      splitHour(rates.data.startHour) || { hour: "", minute: "", period: "AM" },
  );
  const [endHourObj, setEndHourObj] = useState(
    () =>
      splitHour(rates.data.endHour) || { hour: "", minute: "", period: "AM" },
  );

  const [startHour2Obj, setStartHour2Obj] = useState(
    () =>
      splitHour(rates.data.startHour2) || { hour: "", minute: "", period: "AM" },
  );
  const [endHour2Obj, setEndHour2Obj] = useState(
    () =>
      splitHour(rates.data.endHour2) || { hour: "", minute: "", period: "AM" },
  );

  useEffect(() => {
    if (rates.data) {
      setStartHourObj(splitHour(rates.data.startHour));
      setEndHourObj(splitHour(rates.data.endHour));
      setStartHour2Obj(splitHour(rates.data.startHour2));
      setEndHour2Obj(splitHour(rates.data.endHour2));
    }
  }, [rates.data?.id]);

  const updateStartHour = (newPart) => {
    const updated = { ...startHourObj, ...newPart };
    setStartHourObj(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.updateData({
      startHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`,
    });
  };

  const updateEndHour = (newPart) => {
    const updated = { ...endHourObj, ...newPart };
    setEndHourObj(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.updateData({
      endHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`,
    });
  };

  const updateStartHour2 = (newPart) => {
    const updated = { ...startHour2Obj, ...newPart };
    setStartHour2Obj(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.updateData({
      startHour2: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`,
    });
  };

  const updateEndHour2 = (newPart) => {
    const updated = { ...endHour2Obj, ...newPart };
    setEndHour2Obj(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.updateData({
      endHour2: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`,
    });
  };

  const [gn1Start, setGn1Start] = useState({ hour: "08", minute: "00", period: "PM" });
  const [gn1End, setGn1End] = useState({ hour: "12", minute: "00", period: "AM" });
  const [gn2Start, setGn2Start] = useState({ hour: "12", minute: "00", period: "AM" });
  const [gn2End, setGn2End] = useState({ hour: "04", minute: "00", period: "AM" });

  useEffect(() => {
    if (rates.generalNightTariff1) {
      setGn1Start(splitHour(rates.generalNightTariff1.startHour));
      setGn1End(splitHour(rates.generalNightTariff1.endHour));
    }
    if (rates.generalNightTariff2) {
      setGn2Start(splitHour(rates.generalNightTariff2.startHour));
      setGn2End(splitHour(rates.generalNightTariff2.endHour));
    }
  }, [rates.rowSelected]);

  const updateGn1Start = (newPart) => {
    const updated = { ...gn1Start, ...newPart };
    setGn1Start(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.setGeneralNightTariff1(prev => ({
      ...prev,
      startHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`
    }));
  };
  const updateGn1End = (newPart) => {
    const updated = { ...gn1End, ...newPart };
    setGn1End(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.setGeneralNightTariff1(prev => ({
      ...prev,
      endHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`
    }));
  };

  const updateGn2Start = (newPart) => {
    const updated = { ...gn2Start, ...newPart };
    setGn2Start(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.setGeneralNightTariff2(prev => ({
      ...prev,
      startHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`
    }));
  };
  const updateGn2End = (newPart) => {
    const updated = { ...gn2End, ...newPart };
    setGn2End(updated);
    const h = updated.hour === "" ? "00" : updated.hour;
    const m = updated.minute === "" ? "00" : updated.minute;
    rates.setGeneralNightTariff2(prev => ({
      ...prev,
      endHour: `${h.padStart(2, "0")}:${m.padStart(2, "0")} ${updated.period}`
    }));
  };

  if (rates.rowSelected === "GENERAL") {
    return (
      <styles.MainContainer style={{ justifyContent: "center", padding: "20px 0" }}>
        <div style={{
          width: "100%",
          maxWidth: "600px",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          padding: "32px",
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.06)",
          border: "1px solid #f0f0f5",
          display: "flex",
          flexDirection: "column",
          gap: "24px"
        }}>
          {/* HEADER */}
          <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid #f0f0f5", paddingBottom: "16px" }}>
            <div
              style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
              onClick={() => rates.onChangeRowSelected(-1)}
            >
              <VeryIconGoogle
                name="chevron_left"
                size={28}
                color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
                type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
              />
            </div>
            <VeryIconGoogle
              name="paid"
              size={24}
              color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
              type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
            />
            <span style={{ fontSize: "18px", fontWeight: "700", color: "#1f2937" }}>
              Editar Tarifa General
            </span>
          </div>

          {/* SECCIÓN 1: TARIFA GENERAL */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Tarifa de Distancia General
            </span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
              <VeryInput
                title="Tarifa Base ($)"
                isEditable
                placeHolder="Ej: 45"
                text={rates.generalBasePrice}
                onChangeText={(v) => rates.setGeneralBasePrice(v)}
              />
              <VeryInput
                title="Kilómetros Base"
                isEditable
                placeHolder="Ej: 3"
                text={rates.generalBaseKm}
                onChangeText={(v) => rates.setGeneralBaseKm(v)}
              />
              <VeryInput
                title="Km Extra ($)"
                isEditable
                placeHolder="Ej: 10"
                text={rates.generalExtraKmPrice}
                onChangeText={(v) => rates.setGeneralExtraKmPrice(v)}
              />
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid #f0f0f5", margin: "8px 0" }} />

          {/* SECCIÓN 2: TARIFA NOCTURNA 1 */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Tarifa Nocturna General 1
              </span>
              <VerySwitch
                selected={rates.generalNightTariff1.active}
                onChangeSelected={(selected) => rates.setGeneralNightTariff1(prev => ({ ...prev, active: selected }))}
              />
            </div>
            <div style={{
              opacity: rates.generalNightTariff1.active ? 1 : 0.4,
              transition: "opacity 0.2s",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "16px",
              backgroundColor: "#f9fafb",
              borderRadius: "12px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "13px", color: "#374151" }}>Costo adicional nocturno ($)</span>
                <div style={{ display: "flex", alignItems: "center", backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "4px 8px" }}>
                  <span style={{ marginRight: "4px", color: "#6b7280" }}>$</span>
                  <input
                    type="text"
                    style={{ border: "none", outline: "none", width: "60px", fontWeight: "600", fontSize: "14px" }}
                    value={rates.generalNightTariff1.increment}
                    disabled={!rates.generalNightTariff1.active}
                    onChange={(e) => rates.setGeneralNightTariff1(prev => ({ ...prev, increment: e.target.value }))}
                  />
                  <span style={{ color: "#6b7280", fontSize: "12px" }}>MXN</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* Start hour */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600" }}>Hora de Inicio</span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn1Start.hour}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1Start({ hour: e.target.value })}
                    />
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn1Start.minute}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1Start({ minute: e.target.value })}
                    />
                    <select
                      value={gn1Start.period}
                      style={{ border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1Start({ period: e.target.value })}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                {/* End hour */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600" }}>Hora de Fin</span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn1End.hour}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1End({ hour: e.target.value })}
                    />
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn1End.minute}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1End({ minute: e.target.value })}
                    />
                    <select
                      value={gn1End.period}
                      style={{ border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      disabled={!rates.generalNightTariff1.active}
                      onChange={(e) => updateGn1End({ period: e.target.value })}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SECCIÓN 3: TARIFA NOCTURNA 2 */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Tarifa Nocturna General 2
              </span>
              <VerySwitch
                selected={rates.generalNightTariff2.active}
                onChangeSelected={(selected) => rates.setGeneralNightTariff2(prev => ({ ...prev, active: selected }))}
              />
            </div>
            <div style={{
              opacity: rates.generalNightTariff2.active ? 1 : 0.4,
              transition: "opacity 0.2s",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              padding: "16px",
              backgroundColor: "#f9fafb",
              borderRadius: "12px",
              border: "1px solid #e5e7eb"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
                <span style={{ fontSize: "13px", color: "#374151" }}>Costo adicional nocturno ($)</span>
                <div style={{ display: "flex", alignItems: "center", backgroundColor: "#ffffff", border: "1px solid #d1d5db", borderRadius: "8px", padding: "4px 8px" }}>
                  <span style={{ marginRight: "4px", color: "#6b7280" }}>$</span>
                  <input
                    type="text"
                    style={{ border: "none", outline: "none", width: "60px", fontWeight: "600", fontSize: "14px" }}
                    value={rates.generalNightTariff2.increment}
                    disabled={!rates.generalNightTariff2.active}
                    onChange={(e) => rates.setGeneralNightTariff2(prev => ({ ...prev, increment: e.target.value }))}
                  />
                  <span style={{ color: "#6b7280", fontSize: "12px" }}>MXN</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: "16px" }}>
                {/* Start hour */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600" }}>Hora de Inicio</span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn2Start.hour}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2Start({ hour: e.target.value })}
                    />
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn2Start.minute}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2Start({ minute: e.target.value })}
                    />
                    <select
                      value={gn2Start.period}
                      style={{ border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2Start({ period: e.target.value })}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
                {/* End hour */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "6px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600" }}>Hora de Fin</span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn2End.hour}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2End({ hour: e.target.value })}
                    />
                    <input
                      type="text"
                      maxLength={2}
                      style={{ width: "36px", textAlign: "center", border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      value={gn2End.minute}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2End({ minute: e.target.value })}
                    />
                    <select
                      value={gn2End.period}
                      style={{ border: "1px solid #d1d5db", borderRadius: "6px", padding: "4px" }}
                      disabled={!rates.generalNightTariff2.active}
                      onChange={(e) => updateGn2End({ period: e.target.value })}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: "12px", color: "#6b7280", lineHeight: "1.5", marginTop: "8px" }}>
            * La tarifa general se calcula tomando la <strong>Tarifa Base</strong> para los primeros <strong>Kilómetros Base</strong>, y luego aplicando incrementos del <strong>Precio por Kilómetro Extra</strong> por cada fracción de distancia adicional según la fórmula escalonada del sistema.
          </div>

          {/* SAVE BUTTON */}
          <div style={{ marginTop: "16px" }}>
            <VeryBtnFill
              label="Guardar Configuración General"
              colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
              textSize={14}
              onClick={rates.saveGeneralRateConfig}
            />
          </div>
        </div>
      </styles.MainContainer>
    );
  }

  return (
    <>
      {isStringEmpty(rates.data.id) ? null : (
        <styles.MainContainer>
          <styles.ContainerLeftEditRate>
            {/* HEADER */}
            <div className="divHeaderRight">
              <div className="divTitle">
                <div
                  className="divIcon"
                  onClick={() => rates.onChangeRowSelected(-1)}
                >
                  <VeryIconGoogle
                    name="chevron_left"
                    size={25}
                    color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
                    type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                  />
                </div>
                <VeryIconGoogle
                  name="map"
                  size={15}
                  color={classVeryIconGoogle.ICON_COLOR.BLACK_S1}
                  type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                />
                <span>Tarifa dinámica</span>
              </div>
              <div className="divRate">
                <div className="divRateTitle">
                  <VeryIconGoogle
                    name="paid"
                    type={classVeryIconGoogle.ICON_TYPE.FILLED}
                    size={25}
                    color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                  />
                  <span>Tarifa General</span>
                  <Tooltip
                    className="tooltip"
                    title="Tarifa inicial general es el precio asignado por la aplicacion a nivel general"
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <div className="divRateValue">
                  <span className="">$ 45.00 km</span>
                </div>
              </div>
            </div>
            {/* INPUTS */}
            <div className="divInputs">
              <div className="divInputText">
                <span>Nombre</span>
                <hr />
                <input
                  type="text"
                  value={rates.data.name}
                  onChange={(newName) =>
                    rates.updateData({ name: newName.target.value })
                  }
                />
              </div>
              <div className="divInputLocation">
                <div className="divLocationTitle">
                  <span>Ubicación</span>
                  <Tooltip
                    className="tooltip"
                    title="Ubicación es el área asignada para activar la tarifa dinámica"
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <hr />
                <InputSearch
                  CSS={["inputLocation"]}
                  value={rates.data.ubicationName}
                  isLoaded={props.isLoaded}
                  onSelectLocation={(location) =>
                    rates.updateData({
                      ubicationName: location.name,
                      ubicationCoordinates: {
                        lat: parseFloat(location.lat),
                        lng: parseFloat(location.lng),
                      },
                    })
                  }
                />
              </div>
              <div className="divInputText">
                <div className="divInputTitle">
                  <span>Tarifa Inicial</span>
                  <Tooltip
                    className="tooltip"
                    title="Precio fijo al inicio del viaje y km cubiertos por ese precio. Si el viaje supera esos km y la tarifa dinámica está activa, se cobra extra."
                    placement="top"
                  >
                    <div>
                      <VeryIconGoogle
                        name="info"
                        type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                        size={20}
                        color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                      />
                    </div>
                  </Tooltip>
                </div>
                <hr />
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <span style={{ fontSize: 12, color: "#8f8f8f" }}>$</span>
                    <input
                      type="text"
                      value={rates.data.initialRate}
                      style={{ width: 60 }}
                      onChange={(e) =>
                        rates.updateData({ initialRate: e.target.value })
                      }
                    />
                    <span style={{ fontSize: 11, color: "#8f8f8f" }}>MXN</span>
                  </div>
                  <span style={{ color: "#d1d5db", fontWeight: 300 }}>|</span>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    <input
                      type="text"
                      value={rates.data.kilometers}
                      style={{ width: 42 }}
                      placeholder="1"
                      onChange={(e) =>
                        rates.updateData({ kilometers: e.target.value })
                      }
                    />
                    <span style={{ fontSize: 11, color: "#8f8f8f" }}>km</span>
                  </div>
                </div>
              </div>
            </div>
            {/* CONTENT MAP */}
            <div className="divMap">
              <div className="divHeaderMap">
                <span>Ubicación</span>
                {/* Color inline junto al botón */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginLeft: "auto",
                  }}
                >
                  <span
                    style={{ fontSize: 12, fontWeight: 700, color: "#444" }}
                  >
                    Color
                  </span>
                  <ColorPicker
                    valueColor={rates.data.color}
                    onChangeColor={(color) => rates.updateData({ color })}
                  />
                  <VeryBtnFill
                    colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
                    label="Editar geocerca"
                    icon={{
                      name: "edit_location_alt",
                      size: 15,
                      type: classVeryIconGoogle.ICON_TYPE.OUTLINED,
                    }}
                    onClick={() => rates.showModalEditGeofence()}
                  />
                </div>
              </div>
              <GeofenceMap
                isLoaded={props.isLoaded}
                center={mapCenter}
                singlePolygon={
                  Array.isArray(rates.data.polygons) &&
                  rates.data.polygons.length > 0
                    ? { color: rates.data.color, polygons: rates.data.polygons }
                    : null
                }
                zoom={16}
                height="50vh"
                width="100%"
                mode="view"
                onPolygonUpdate={(polygons) =>
                  rates.updateData({ polygons: polygons })
                }
              />
            </div>
          </styles.ContainerLeftEditRate>
          <styles.ContainerRightEditRate>
            <VeryBtnFill
              colorTheme={classVeryBtnFill.COLOR_THEME.BLACK_S1}
              label="Guardar"
              onClick={() => rates.onClickBtnSave()}
            />
            {/* TARIFA DINAMICA */}
            <div className="divDynamicRate">
              <div className="divSwitchDynamic">
                <VerySwitch
                  selected={rates.data.isDynamic}
                  onChangeSelected={(selected) => {
                    if (!selected) {
                      setConfirmDynamicOpen(true);
                    } else {
                      rates.updateData({ isDynamic: true });
                    }
                  }}
                />
                <span>Tarifa Dinámica</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa Dinámica el costo aumenta de acuerdo a los parametros de distancia, horario y demanda "
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <hr />
              <div className="divPricePerKilometerTitle">
                <span>Precio sobre kilómetros</span>
                <Tooltip
                  className="tooltip"
                  title="Tarifa de distancia se activa cuando se rebasan los limites permitidos de kilometros"
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className="divPricePerKilometerInputs">
                <span>$</span>
                <input
                  type="text"
                  value={rates.data.pricePerKilometer}
                  onChange={(e) =>
                    rates.updateData({ pricePerKilometer: e.target.value })
                  }
                />
                <span style={{ color: "#8f8f8f", fontSize: 12 }}> / 1 km</span>
              </div>
              <hr />
              <div style={{
                padding: "14px",
                backgroundColor: "rgba(255, 152, 0, 0.08)",
                border: "1px solid rgba(255, 152, 0, 0.25)",
                borderRadius: "8px",
                fontSize: "12.5px",
                color: "#5c5c66",
                lineHeight: "1.5",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                marginTop: "15px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#f57c00", fontWeight: "700" }}>
                  <VeryIconGoogle
                    name="info"
                    type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                    size={18}
                    color={classVeryIconGoogle.ICON_COLOR.ORANGE_S1}
                  />
                  <span>Tarifas por horarios generales</span>
                </div>
                <span>
                  Las tarifas nocturnas generales configuradas en el sistema se sumarán y aplicarán automáticamente por defecto sobre los viajes de esta geocerca:
                </span>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", backgroundColor: "#fff", padding: "8px 12px", borderRadius: "6px", border: "1px solid #ffe0b2" }}>
                  {rates.generalNightTariff1?.active ? (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span><strong>Tarifa Nocturna 1:</strong> {rates.generalNightTariff1.startHour} a {rates.generalNightTariff1.endHour}</span>
                      <span style={{ color: "#e65100", fontWeight: "700" }}>+{rates.generalNightTariff1.increment} MXN</span>
                    </div>
                  ) : null}
                  {rates.generalNightTariff2?.active ? (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", borderTop: rates.generalNightTariff1?.active ? "1px solid #ffe0b2" : "none", paddingTop: rates.generalNightTariff1?.active ? "4px" : "0" }}>
                      <span><strong>Tarifa Nocturna 2:</strong> {rates.generalNightTariff2.startHour} a {rates.generalNightTariff2.endHour}</span>
                      <span style={{ color: "#e65100", fontWeight: "700" }}>+{rates.generalNightTariff2.increment} MXN</span>
                    </div>
                  ) : null}
                  {!rates.generalNightTariff1?.active && !rates.generalNightTariff2?.active ? (
                    <span style={{ fontSize: "12px", color: "#888", fontStyle: "italic" }}>No hay tarifas nocturnas generales activas actualmente.</span>
                  ) : null}
                </div>
              </div>
            </div>
            {/* GRÁFICA — reemplaza sección Color */}
            <div className="divColor" style={{ gap: 8 }}>
              <RatePreviewChart
                initialRate={rates.data.initialRate}
                kilometers={rates.data.kilometers}
                pricePerKilometer={rates.data.pricePerKilometer}
                isDynamic={rates.data.isDynamic}
                priceOnDemand={
                  rates.data.isDemand ? rates.data.priceOnDemand : 0
                }
                width={240}
              />
            </div>
            {/* PRIORIDAD */}
            <div className="divPriority">
              <div className="divPriorityTitle">
                <span>Prioridad</span>
                <Tooltip
                  className="tooltip"
                  title="Cuando un viaje cae en varias geocercas, se aplica la de mayor prioridad. 1 = máxima, 3 = mínima."
                  placement="top"
                >
                  <div>
                    <VeryIconGoogle
                      name="info"
                      type={classVeryIconGoogle.ICON_TYPE.OUTLINED}
                      size={20}
                      color={classVeryIconGoogle.ICON_COLOR.GRAY_S1}
                    />
                  </div>
                </Tooltip>
              </div>
              <hr />
              <select
                value={rates.data.priority}
                onChange={(e) => rates.updateData({ priority: e.target.value })}
                style={{
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                }}
              >
                <option value="principal">1 — Principal (máxima)</option>
                <option value="secundario">2 — Secundaria</option>
                <option value="terciario">3 — Terciaria (mínima)</option>
              </select>
            </div>
          </styles.ContainerRightEditRate>
        </styles.MainContainer>
      )}
      <Dialog
        open={confirmDynamicOpen}
        onClose={() => setConfirmDynamicOpen(false)}
      >
        <DialogTitle>¿Desactivar tarifa dinámica?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Al desactivar la tarifa dinámica,{" "}
            <strong>
              todos los viajes en esta geocerca cobrarán únicamente el monto
              base fijo
            </strong>{" "}
            (${rates.data.initialRate} MXN) sin importar la distancia recorrida.
            ¿Estás seguro?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDynamicOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => {
              rates.updateData({ isDynamic: false });
              setConfirmDynamicOpen(false);
            }}
          >
            Sí, desactivar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ContainerRightRate;
